import { useCallback } from 'react';

/**
 * requestIds das mutations cujo erro a tela trata por conta própria. A
 * ErrorMiddleware consulta este conjunto para não somar um toast global à
 * mensagem que a tela já exibe.
 */
const suppressedRequestIds = new Set<string>();

export const isErrorSuppressed = (requestId?: string): boolean =>
  !!requestId && suppressedRequestIds.has(requestId);

type TrackedRequest = PromiseLike<unknown> & { requestId: string };

/**
 * Wrapper para mutations do RTK Query que permite controlar a exibição de alertas de erro.
 *
 * Recebe o trigger do RTK Query (não uma lambda que já chamou `unwrap()`), porque
 * é do objeto devolvido pelo trigger que sai o `requestId` usado para casar a
 * supressão com a request exata.
 *
 * @example
 * const [createCompany, result] = useCreateCompanyMutation();
 * const createCompanyWithControl = useMutationWithErrorControl(createCompany);
 *
 * // Com alerta de erro (padrão)
 * await createCompanyWithControl(companyData);
 *
 * // Sem alerta de erro
 * await createCompanyWithControl(companyData, { suppressErrorAlert: true });
 *
 * // Sem alerta de erro e tratando a rejeição na tela
 * await createCompanyWithControl(companyData, { suppressErrorAlert: true }).unwrap();
 */
export const useMutationWithErrorControl = <
  TArg,
  TRequest extends TrackedRequest
>(
  mutationTrigger: (arg: TArg) => TRequest
) =>
  useCallback(
    (arg: TArg, options?: { suppressErrorAlert?: boolean }): TRequest => {
      // O trigger despacha a action `pending` de forma síncrona, então o
      // requestId já existe aqui -- muito antes de a resposta chegar.
      const request = mutationTrigger(arg);

      if (!options?.suppressErrorAlert) {
        return request;
      }

      const { requestId } = request;
      suppressedRequestIds.add(requestId);

      // A middleware lê o conjunto durante o dispatch do `rejected`, que acontece
      // antes de a promise assentar; logo, limpar aqui não corre o risco de
      // remover o requestId antes de ele ser consultado.
      const release = () => {
        suppressedRequestIds.delete(requestId);
      };
      request.then(release, release);

      return request;
    },
    [mutationTrigger]
  );
